import { useMemo, useState } from "react";
import type { RoleDefinition } from "../../app/roles";
import { StatusPill } from "../../components/StatusPill";
import { GUIDED_OPERATIONS_FIXTURE } from "../../data/guidedOperations";
import { responseOptions } from "../../data/seed";
import type {
  OverridableReadinessRuleId,
  ReadinessFacts,
  ReadinessResult,
  ResponseOption,
} from "../../domain";
import {
  selectReadinessResults,
  summarizeReadiness,
} from "../../simulation/guidedOperationsSelectors";
import { useScenario } from "../../simulation/ScenarioContext";
import "./SafetyHumanGate.css";

type SafetyHumanGateProps = {
  pendingDecision: ResponseOption["id"] | null;
  role: RoleDefinition;
  onDecisionRecorded: () => void;
};

type VerificationSelection = "" | "pass" | "not-passed";

const OWNER = {
  id: "USR-MAYA",
  name: "Maya Chen",
} as const;

const APPROVER = {
  id: "USR-ELENA-TORRES",
  name: "Elena Torres",
} as const;

const warningLabels: Record<OverridableReadinessRuleId, string> = {
  "redundancy-known": "limited-redundancy warning",
  "maintenance-window": "emergency-window warning",
};

function actorForRole(role: RoleDefinition) {
  if (role.id === "technical-ii") return OWNER.name;
  if (role.id === "department-lead") return APPROVER.name;
  return role.label;
}

function toneForResult(result: ReadinessResult) {
  if (result.status === "pass") return "healthy" as const;
  if (result.status === "warning") return "warning" as const;
  return "critical" as const;
}

export function SafetyHumanGate({
  pendingDecision,
  role,
  onDecisionRecorded,
}: SafetyHumanGateProps) {
  const {
    acknowledgedBy,
    assignIncidentOwner,
    assignedOwner,
    decideResponse,
    humanDecision,
    linkedEvidenceIds,
    readinessOverrides,
    recordReadinessOverride,
    recoveryVerification,
    selectedDecision,
    stage,
    verifyRecovery,
  } = useScenario();
  const [overrideReasons, setOverrideReasons] = useState<
    Record<OverridableReadinessRuleId, string>
  >({
    "redundancy-known": "",
    "maintenance-window": "",
  });
  const [decisionReason, setDecisionReason] = useState("");
  const [serviceCheck, setServiceCheck] = useState<VerificationSelection>("");
  const [endpointCheck, setEndpointCheck] = useState<VerificationSelection>("");

  const actor = actorForRole(role);
  const facts = useMemo<ReadinessFacts>(
    () => ({
      acknowledgedBy,
      ownerId: assignedOwner?.id ?? null,
      linkedEvidenceCount: linkedEvidenceIds.length,
      requiredEvidenceCount: GUIDED_OPERATIONS_FIXTURE.readiness.requiredEvidenceCount,
      redundancyState: GUIDED_OPERATIONS_FIXTURE.readiness.redundancyState,
      maintenanceWindow: GUIDED_OPERATIONS_FIXTURE.readiness.maintenanceWindow,
      approver: role.canApproveSensitiveChanges
        ? {
            ...APPROVER,
            canApproveSensitiveChanges: true,
          }
        : null,
    }),
    [
      acknowledgedBy,
      assignedOwner?.id,
      linkedEvidenceIds.length,
      role.canApproveSensitiveChanges,
    ],
  );
  const readiness = useMemo(() => selectReadinessResults(facts), [facts]);
  const summary = useMemo(
    () => summarizeReadiness(readiness, readinessOverrides),
    [readiness, readinessOverrides],
  );
  const proposedResponse = responseOptions.find(
    (option) => option.id === pendingDecision,
  ) ?? null;
  const approvedResponse = selectedDecision
    ?? responseOptions.find((option) => option.id === humanDecision?.responseId)
    ?? null;
  const hasDecisionReason = decisionReason.trim().length >= 12;
  const canApprove = Boolean(
    proposedResponse
    && role.canApproveSensitiveChanges
    && summary.confirmable
    && hasDecisionReason,
  );
  const canReject = Boolean(
    proposedResponse
    && role.canApproveSensitiveChanges
    && hasDecisionReason,
  );
  const verificationReady = serviceCheck !== "" && endpointCheck !== "";
  const verificationComplete = Boolean(
    recoveryVerification?.serviceRecovered
    && recoveryVerification.endpointReregistered,
  );

  function recordOverride(ruleId: OverridableReadinessRuleId) {
    const reason = overrideReasons[ruleId].trim();
    if (!reason) return;
    recordReadinessOverride(
      ruleId,
      APPROVER.name,
      reason,
      role.canApproveSensitiveChanges,
    );
    setOverrideReasons((current) => ({ ...current, [ruleId]: "" }));
  }

  function recordDecision(decision: "approved" | "rejected") {
    if (!proposedResponse) return;
    decideResponse({
      actor: APPROVER.name,
      approverId: APPROVER.id,
      approverName: APPROVER.name,
      canApproveSensitiveChanges: role.canApproveSensitiveChanges,
      decision,
      reason: decisionReason.trim(),
      responseId: proposedResponse.id,
    });
    setDecisionReason("");
    onDecisionRecorded();
  }

  function recordVerification() {
    if (!verificationReady) return;
    const serviceRecovered = serviceCheck === "pass";
    const endpointReregistered = endpointCheck === "pass";
    verifyRecovery(
      actor,
      serviceRecovered,
      endpointReregistered,
      serviceRecovered && endpointReregistered
        ? "Service checks passed and TEL-0318 re-registered."
        : "One or more recovery checks did not pass; monitoring continues.",
    );
    setServiceCheck("");
    setEndpointCheck("");
  }

  return (
    <section className="safety-gate" aria-labelledby="safety-gate-title">
      <header className="safety-gate__header">
        <div>
          <p className="eyebrow">Governed response gate</p>
          <h3 id="safety-gate-title">Safety, ownership, and human decision</h3>
          <p>
            Consequential recovery cannot run until blocked checks are cleared
            and both warning overrides have accountable receipts.
          </p>
        </div>
        <StatusPill
          label={summary.confirmable ? "Ready for decision" : "Not ready"}
          tone={summary.confirmable ? "healthy" : "warning"}
        />
      </header>

      <div className="safety-gate__ownership" aria-label="Acknowledgement and ownership">
        <article>
          <div>
            <span className="field-label">Accountable owner</span>
            <strong>{assignedOwner?.name ?? "No owner assigned"}</strong>
            <p>
              {assignedOwner
                ? `${assignedOwner.name} remains accountable through recovery.`
                : "The technical response needs one explicit owner."}
            </p>
          </div>
          {!assignedOwner && acknowledgedBy && (
            <button
              className="button button--secondary"
              disabled={!role.canManageIncidents}
              onClick={() => assignIncidentOwner(
                actor,
                OWNER.id,
                OWNER.name,
                "Keep one accountable technical owner through recovery.",
              )}
              type="button"
            >
              Assign {OWNER.name}
            </button>
          )}
        </article>

        <article>
          <div>
            <span className="field-label">Acknowledgement</span>
            <strong>{acknowledgedBy ?? "Not acknowledged"}</strong>
            <p>
              {acknowledgedBy
                ? "Initial triage responsibility is recorded."
                : "A named operator must accept initial triage."}
            </p>
          </div>
          {!acknowledgedBy && (
            <button
              className="button button--secondary"
              disabled={!role.canManageIncidents}
              onClick={() => assignIncidentOwner(
                actor,
                OWNER.id,
                OWNER.name,
                "Accepted finals-week incident triage and ownership.",
              )}
              type="button"
            >
              Acknowledge and assign {OWNER.name}
            </button>
          )}
        </article>
      </div>

      {!role.canManageIncidents && (!acknowledgedBy || !assignedOwner) && (
        <p className="blocked-explanation">
          {role.label} may inspect and link evidence. Switch to Technical II or
          Department Lead to acknowledge and assign the incident.
        </p>
      )}

      <div className="safety-gate__readiness">
        <header>
          <div>
            <span className="field-label">Readiness review</span>
            <h4>Six checks, with every consequence visible</h4>
          </div>
          <span>
            {summary.blockedCount} blocked · {summary.unresolvedWarningCount} unresolved warning
            {summary.unresolvedWarningCount === 1 ? "" : "s"}
          </span>
        </header>
        <ul>
          {readiness.map((result) => {
            const override = readinessOverrides.find(
              (item) => item.ruleId === result.ruleId,
            );
            const ruleId = result.canOverride
              ? result.ruleId as OverridableReadinessRuleId
              : null;
            return (
              <li className={`readiness-item readiness-item--${result.status}`} key={result.ruleId}>
                <div className="readiness-item__status">
                  <StatusPill
                    label={override ? "Override recorded" : result.status}
                    tone={override ? "healthy" : toneForResult(result)}
                  />
                </div>
                <div>
                  <strong>{result.label}</strong>
                  <p>{result.reason}</p>
                  <small>{override ? `Receipt reason: ${override.reason}` : result.nextAction}</small>
                  {ruleId && !override && (
                    <div className="readiness-override">
                      <label htmlFor={`override-${ruleId}`}>
                        Reason for accepting this {warningLabels[ruleId]}
                      </label>
                      <textarea
                        disabled={!role.canApproveSensitiveChanges}
                        id={`override-${ruleId}`}
                        onChange={(event) => setOverrideReasons((current) => ({
                          ...current,
                          [ruleId]: event.target.value,
                        }))}
                        placeholder="State the risk, safeguard, and why proceeding is justified."
                        rows={3}
                        value={overrideReasons[ruleId]}
                      />
                      <button
                        className="button button--secondary"
                        disabled={
                          !role.canApproveSensitiveChanges
                          || overrideReasons[ruleId].trim().length < 12
                          || stage !== "identified"
                        }
                        onClick={() => recordOverride(ruleId)}
                        type="button"
                      >
                        Record override receipt
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {!role.canApproveSensitiveChanges && (
        <p className="blocked-explanation">
          A Department Lead must review warning overrides and make the human
          decision. No outcome is preselected.
        </p>
      )}

      <div className="human-decision" aria-labelledby="human-decision-title">
        <header>
          <div>
            <span className="field-label">Human approval</span>
            <h4 id="human-decision-title">
              {proposedResponse
                ? `Decide whether to execute ${proposedResponse.label}`
                : "Select a response plan for human review"}
            </h4>
          </div>
          {humanDecision && (
            <StatusPill
              label={`Last decision: ${humanDecision.decision}`}
              tone={humanDecision.decision === "approved" ? "healthy" : "warning"}
            />
          )}
        </header>

        {proposedResponse ? (
          <>
            <div className="human-decision__consequence">
              <strong>{proposedResponse.label}</strong>
              <p>{proposedResponse.result}</p>
              <dl>
                <div><dt>Projected recovery</dt><dd>{proposedResponse.recoveryPercent}%</dd></div>
                <div><dt>Time</dt><dd>{proposedResponse.minutesToRestore} min</dd></div>
                <div><dt>Risk</dt><dd>{proposedResponse.risk}</dd></div>
              </dl>
            </div>
            <label htmlFor="human-decision-reason">
              Decision reason
            </label>
            <textarea
              disabled={!role.canApproveSensitiveChanges}
              id="human-decision-reason"
              onChange={(event) => setDecisionReason(event.target.value)}
              placeholder="Explain why this response should or should not proceed."
              rows={3}
              value={decisionReason}
            />
            <div className="human-decision__choices">
              <button
                className="human-decision__choice"
                disabled={!canApprove}
                onClick={() => recordDecision("approved")}
                type="button"
              >
                <strong>Approve response</strong>
                <span>Execute the plan, record the decision, and move into monitored recovery.</span>
              </button>
              <button
                className="human-decision__choice"
                disabled={!canReject}
                onClick={() => recordDecision("rejected")}
                type="button"
              >
                <strong>Reject response</strong>
                <span>Do not execute the plan; preserve evidence and return it for revision.</span>
              </button>
            </div>
            {!summary.confirmable && (
              <p className="human-decision__note">
                Approval remains blocked by {summary.blockedCount} blocked check
                {summary.blockedCount === 1 ? "" : "s"} and {summary.unresolvedWarningCount} unresolved warning
                {summary.unresolvedWarningCount === 1 ? "" : "s"}. Rejection remains available to the named approver.
              </p>
            )}
          </>
        ) : (
          <p className="empty-copy">
            Compare the response options above and select one. Selection proposes
            a plan; it does not execute or pre-approve it.
          </p>
        )}
      </div>

      {humanDecision?.decision === "approved" && approvedResponse && (
        <div className="recovery-verification" aria-labelledby="recovery-verification-title">
          <header>
            <div>
              <span className="field-label">Recovery verification</span>
              <h4 id="recovery-verification-title">Confirm service and TEL-0318 separately</h4>
            </div>
            <StatusPill
              label={verificationComplete ? "Recovery verified" : "Monitoring"}
              tone={verificationComplete ? "healthy" : "warning"}
            />
          </header>
          {verificationComplete ? (
            <p>
              {recoveryVerification?.verifiedBy} verified service recovery and
              TEL-0318 re-registration. The verification receipt is preserved.
            </p>
          ) : (
            <>
              <div className="recovery-verification__checks">
                <label>
                  Service recovery result
                  <select
                    onChange={(event) => setServiceCheck(event.target.value as VerificationSelection)}
                    value={serviceCheck}
                  >
                    <option value="">Choose a result</option>
                    <option value="pass">Passed — service restored</option>
                    <option value="not-passed">Not passed — continue monitoring</option>
                  </select>
                </label>
                <label>
                  TEL-0318 registration result
                  <select
                    onChange={(event) => setEndpointCheck(event.target.value as VerificationSelection)}
                    value={endpointCheck}
                  >
                    <option value="">Choose a result</option>
                    <option value="pass">Passed — endpoint registered</option>
                    <option value="not-passed">Not passed — registration absent</option>
                  </select>
                </label>
              </div>
              <button
                className="button button--primary"
                disabled={!verificationReady || !role.canManageIncidents}
                onClick={recordVerification}
                type="button"
              >
                Record verification result
              </button>
              {!role.canManageIncidents && (
                <p className="human-decision__note">
                  Switch to Technical II to record the recovery verification.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
